import {
  BankReconciliationAccountingLine,
  BankReconciliationLineEdit,
} from 'lib/api/accounting/types';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import React, { useEffect, useMemo, useState } from 'react';

import {
  BankReconciliationEntryReference,
  EntryDescriptionCell,
  ObservationCell,
  ReconciledAtCell,
  ReconciledCheckboxCell,
  TableRowData,
} from './components/TableCells';

type BankReconciliationRow = {
  line: BankReconciliationAccountingLine;
  rowKey: string;
  edit: BankReconciliationLineEdit;
};
export type { BankReconciliationEntryReference };

interface BankReconciliationTableProps {
  rows: BankReconciliationRow[];
  totalItems: number;
  page: number;
  rowsPerPage: number;
  emptyEditDate: string;
  formatDate: (value?: string) => string;
  formatAmount: (value?: number) => string;
  onToggleReconciled: (
    rowKey: string,
    isChecked: boolean,
    reconciledAt: string | null,
  ) => void;
  onOpenObservationDialog: (rowKey: string, observation: string) => void;
  onOpenEntry: (reference: BankReconciliationEntryReference) => void;
  onReconciledAtChange: (rowKey: string, reconciledAt: string | null) => void;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (nextLimit: number) => void;
}

const BankReconciliationTable: React.FC<BankReconciliationTableProps> = ({
  rows,
  totalItems,
  page,
  rowsPerPage,
  emptyEditDate,
  formatDate,
  formatAmount,
  onToggleReconciled,
  onOpenObservationDialog,
  onOpenEntry,
  onReconciledAtChange,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const tableData = useMemo<TableRowData[]>(
    () =>
      rows.map(({ rowKey, line, edit }) => ({
        id: rowKey,
        rowKey,
        line,
        edit,
        date: formatDate(line.date),
        businessIdentifier: line.businessIdentifier ?? '-',
        description: line.description ?? '-',
        debit: typeof line.debit === 'number' ? line.debit : 0,
        credit: typeof line.credit === 'number' ? line.credit : 0,
        isReconciled: edit.isReconciled,
        isReconciledLabel: edit.isReconciled ? 'Sí' : 'No',
        observation: edit.observation,
        reconciledAt: edit.reconciledAt ?? emptyEditDate,
      })),
    [rows, formatDate, emptyEditDate],
  );

  const [data, setData] = useState<TableRowData[]>(tableData);

  useEffect(() => {
    setData(tableData.slice(0, rowsPerPage));
  }, [tableData, rowsPerPage]);

  const renderMovementCell = (value: string, row: TableRowData) => (
    <EntryDescriptionCell value={value} row={row} onOpenEntry={onOpenEntry} />
  );

  const renderReconciledCell = (
    _value: string | number | boolean | null | undefined,
    row: TableRowData,
  ) => (
    <ReconciledCheckboxCell row={row} onToggleReconciled={onToggleReconciled} />
  );

  const renderObservationCell = (
    _value: string | number | boolean | null | undefined,
    row: TableRowData,
  ) => (
    <ObservationCell
      row={row}
      onOpenObservationDialog={onOpenObservationDialog}
    />
  );

  const renderReconciledAtCell = (
    _value: string | number | boolean | null | undefined,
    row: TableRowData,
  ) => (
    <ReconciledAtCell
      row={row}
      emptyEditDate={emptyEditDate}
      onReconciledAtChange={onReconciledAtChange}
    />
  );

  const dataHeaders = useMemo<Header[]>(
    () => [
      { label: 'Fecha', key: 'date' },
      { label: 'RUT', key: 'businessIdentifier' },
      {
        label: 'Movimiento',
        key: 'description',
        customComponent: renderMovementCell,
      },
      {
        label: 'Abono',
        key: 'debit',
        customComponent: formatAmount,
      },
      {
        label: 'Cargo',
        key: 'credit',
        customComponent: formatAmount,
      },
      {
        label: 'Conciliado',
        key: 'isReconciled',
        customComponent: renderReconciledCell,
      },
      {
        label: 'Observación',
        key: 'observation',
        customComponent: renderObservationCell,
      },
      {
        label: 'Fecha Cartola',
        key: 'reconciledAt',
        customComponent: renderReconciledAtCell,
      },
    ],
    [
      onOpenEntry,
      onToggleReconciled,
      onOpenObservationDialog,
      emptyEditDate,
      onReconciledAtChange,
      formatAmount,
    ],
  );

  const exportHeaders = useMemo<Header[]>(
    () =>
      dataHeaders.map((header) =>
        header.key === 'isReconciled'
          ? { label: header.label, key: 'isReconciledLabel' }
          : header,
      ),
    [dataHeaders],
  );

  return (
    <TableWithPagination
      data={data}
      setData={setData}
      dataHeaders={dataHeaders}
      exportHeaders={exportHeaders}
      loading={false}
      exportToFile="xlsx"
      pagination={{
        total: totalItems,
        page: page + 1,
        pageSize: rowsPerPage,
        totalPages: Math.max(1, Math.ceil(totalItems / rowsPerPage)),
      }}
      setPagination={() => {}}
      searchData={async ({
        page: nextPage,
        pageSize,
      }: {
        page: number;
        pageSize: number;
      }) => {
        if (nextPage !== page + 1) {
          onPageChange(nextPage - 1);
        }
        if (pageSize !== rowsPerPage) {
          onRowsPerPageChange(pageSize);
        }

        const nextData = data.slice(0, pageSize);
        return {
          pagination: {
            total: totalItems,
            page: nextPage,
            pageSize,
            totalPages: Math.max(1, Math.ceil(totalItems / pageSize)),
          },
          data: nextData,
        };
      }}
    />
  );
};

export default BankReconciliationTable;
